import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Checkbox,
	FormControl,
	FormLabel,
	Stack,
	Collapse,
	IconButton,
	useDisclosure,
	Text,
	Flex,
	Divider,
	useColorMode,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';

import { useCalendarSettings, useFilters } from '../../../../lib/context';
import { useAutoContrastColor } from '../../../../lib';
import { __ } from '../../../../lib/i18n';

interface IFilterComponentProps {
	isDefaultOpened?: boolean;
	filterType: 'EventTypes' | 'EventTags' | 'EventLocations';
	defaultOptions: string[];
	selectedOptions: string[];
	label: string;
}

export const FilterComponent: React.FC<IFilterComponentProps> = ({
	filterType,
	defaultOptions,
	selectedOptions,
	label,
	isDefaultOpened = true,
}) => {
	const { setFilters } = useFilters();
	const { colorMode } = useColorMode();
	const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isDefaultOpened });
	const { settings } = useCalendarSettings();
	const [options, setOptions] = useState<string[]>(defaultOptions || []);
	const [selected, setSelected] = useState<string[]>(selectedOptions || []);
	const { color: contrastColor, ref } = useAutoContrastColor();

	const handleFilterChange = useCallback(
		(selected: string[]) => {
			setFilters((prevFilters) => ({
				...prevFilters,
				[`selected${filterType}`]: selected,
			}));
		},
		[filterType, setFilters]
	);

	const handleCheckboxChange = useCallback(
		(option: string) => {
			setSelected((prevSelected) => {
				const isSelected = prevSelected.includes(option);
				if (isSelected) {
					return prevSelected.filter((item) => item !== option);
				} else {
					return [...prevSelected, option];
				}
			});
		},
		[setSelected]
	);

	const handleSelectAll = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.checked) {
				setSelected([...options]);
			} else {
				setSelected([]);
			}
		},
		[options, setSelected]
	);

	const createcheckboxHandler = useCallback(
		(option: string) => {
			return () => {
				handleCheckboxChange(option);
			};
		},
		[handleCheckboxChange]
	);

	useEffect(() => {
		if (defaultOptions.length > 0) setOptions(defaultOptions);
	}, [defaultOptions]);

	useEffect(() => {
		handleFilterChange(selected);
	}, [selected, handleFilterChange]);

	const validOptions = Array.isArray(defaultOptions) ? defaultOptions : [];
	const validSelectedOptions = Array.isArray(selectedOptions) ? selectedOptions : [];
	const { filtersTextColor, calendarBordersColor } = settings.styles[colorMode];
	return (
		<FormControl w='full' ref={ref}>
			<FormLabel display='flex' alignItems='center' marginInline={0} mb={2}>
				<Text flex='1' fontSize='sm' fontWeight='700' mb='0px !important' color={filtersTextColor}>
					{label}
				</Text>
				<IconButton
					as='span'
					aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
					icon={isOpen ? <MinusIcon /> : <AddIcon />}
					justifyContent='flex-end'
					background='none'
					color={contrastColor}
					onClick={onToggle}
					variant='link'
					size='sm'
					cursor='pointer'
				/>
			</FormLabel>
			<Collapse in={isOpen}>
				<Box marginBottom='1rem !important'>
					<Stack spacing={3}>
						{validOptions.length > 0 && (
							<>
								<Checkbox display='none' />
								<Flex
									alignItems='center'
									justifyContent='space-between'
									mb='0.5rem !important'
									mt='0px !important'
								>
									<Text fontSize='sm' mb='0px !important' color={filtersTextColor}>
										{__('All')}
									</Text>
									<Checkbox
										border={`1px solid ${contrastColor} !important`}
										borderRadius={0}
										size='sm'
										isChecked={validSelectedOptions.length === validOptions.length}
										onChange={handleSelectAll}
									/>
								</Flex>
								{validOptions.map((option) => {
									const handleCheckbox = createcheckboxHandler(option);

									return (
										<Flex
											key={option}
											alignItems='center'
											justifyContent='space-between'
											mt='0px !important'
										>
											<Flex alignItems='center'>
												{label === 'Category' && (
													<Box
														bg={
															settings?.eventCategoryColors?.[option]?.background ||
															'#32C0CD'
														}
														borderRadius='full'
														width='10px'
														height='10px'
														mr={2}
													/>
												)}
												<Text mb='0px !important' color={filtersTextColor}>
													{option}
												</Text>
											</Flex>

											<Checkbox
												border={`1px solid ${contrastColor} !important`}
												borderRadius={0}
												size='sm'
												isChecked={validSelectedOptions.includes(option)}
												onChange={handleCheckbox}
											/>
										</Flex>
									);
								})}
							</>
						)}
					</Stack>
				</Box>
			</Collapse>
			<Divider borderColor={calendarBordersColor} />
		</FormControl>
	);
};
